import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, mergeMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { Logger } from './core/logger.service';
import { I18nService } from './core/i18n.service';
import { UtilsService } from './shared/utils.service';
import { StateService } from './shared/state.service';
import { ApiService } from './shared/api.service';
import { ConstantsService } from './shared/constants.service';
import { ClientResources } from './client-resources.service';
import { merge } from 'rxjs';

/**
 * Logger declaration and initialisation.
 */
const log = new Logger('App');

/**
 * The App Compoent instance.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  /**
   * The constructor instance of the App component.
   *
   * @param router The injected instance of the Router Service.
   * @param activatedRoute The injected instance of the Activated Route Service.
   * @param titleService The injected instance of the Title Service.
   * @param translateService The injected instance of the Translate Service.
   * @param stateService The injected instance of the State Service.
   * @param apiService The injected instance of the Api Service.
   * @param i18nService The injected instance of the I18n Service.
   * @param constantService The injected instance of the Constants Service.
   * @param clientResources The injected instance of the Client Resources Service.
   */
  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: Title,
    private readonly translateService: TranslateService,
    private readonly stateService: StateService,
    private readonly apiService: ApiService,
    private readonly i18nService: I18nService,
    private readonly constantService: ConstantsService,
    private readonly clientResources: ClientResources
  ) {
    /**
     * Setup logger
     */
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.info('init');

    /**
     * Setup translations
     */
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);
    try {
      log.info('get lang');
      const langH = UtilsService.getHashParamFromUrl('lang');
      const langQ = UtilsService.getParameterByName('lang');
      const localeH = UtilsService.getHashParamFromUrl('locale');
      const localeQ = UtilsService.getParameterByName('locale');
      if (langH !== undefined && langH !== null && UtilsService.allowedLanguage(langH)) {
        this.translateService.use(UtilsService.convertLocale(langH));
      }
      if (langQ !== undefined && langQ !== null && UtilsService.allowedLanguage(langQ)) {
        this.translateService.use(UtilsService.convertLocale(langQ));
      }
      if (localeH !== undefined && localeH !== null && UtilsService.allowedLanguage(localeH)) {
        this.translateService.use(UtilsService.convertLocale(localeH));
      }
      if (localeQ !== undefined && localeQ !== null && UtilsService.allowedLanguage(localeQ)) {
        this.translateService.use(UtilsService.convertLocale(localeQ));
      }
    } catch (e) {}

    this.stateService.retrieveState();
    this.setAppCodes();
  }

  /**
   * Ser App Codes Method.
   */
  setAppCodes(): void {
    if (this.stateService.getClientIsTpp()) {
      this.constantService.setAppCode(this.clientResources.getAppCodes().signClient);
    } else {
      this.constantService.setAppCode(this.clientResources.getAppCodes()[this.stateService.getClientId()]);
    }
  }

  /**
   * Initialise the App Component in the state machine.
   */
  ngOnInit() {
    this.integrationTM();
    log.info('ng on init');
    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route: any) => route.outlet === 'primary'),
        mergeMap((route: any) => route.data)
      )
      .subscribe((event: any) => {
        const title = event.title;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });
  }

  /**
   * ThreatMark Login integration.
   */
  public integrationTM(): void {
    const script = document.createElement('script');

    script.src = this.clientResources.getThreatmarkUrl();

    document.head.appendChild(script);
  }
}
